import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RepositoryError } from '@/data/types';
import type { Language } from '@/types/domain';

/**
 * Carga asincrona con estados de carga y error.
 * Evita repetir el mismo try/catch/setState en cada pagina y, sobre todo,
 * evita el fallo clasico de escribir estado sobre un componente ya
 * desmontado cuando el usuario navega antes de que llegue la respuesta.
 */
export function useAsync<T>(
  loader: () => Promise<T>,
  deps: unknown[],
  options: { immediate?: boolean } = {},
): {
  data: T | null;
  loading: boolean;
  error: unknown;
  reload: () => void;
  setData: (value: T | null) => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(options.immediate !== false);
  const [error, setError] = useState<unknown>(null);
  const [nonce, setNonce] = useState(0);

  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    if (options.immediate === false) return;
    let active = true;
    setLoading(true);
    setError(null);

    loaderRef
      .current()
      .then((result) => {
        if (active) setData(result);
      })
      .catch((caught) => {
        if (active) setError(caught);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { data, loading, error, reload, setData };
}

/**
 * Traduce cualquier error a un mensaje del idioma activo.
 * Los `RepositoryError` llevan clave, no texto, precisamente para esto
 * (RF-LANG-03: tambien los mensajes de error cambian de idioma).
 */
export function useErrorMessage(): (error: unknown) => string {
  const { t } = useTranslation();

  return useCallback(
    (error: unknown): string => {
      if (error instanceof RepositoryError) {
        return t(`errors.${error.code}`, {
          ...error.params,
          defaultValue: t('errors.generic'),
        });
      }
      if (error instanceof TypeError && error.message.includes('fetch')) {
        return t('errors.network');
      }
      return t('errors.generic');
    },
    [t],
  );
}

/** Idioma activo, tipado, para pasarlo a los formateadores de `format.ts`. */
export function useLanguage(): Language {
  const { i18n } = useTranslation();
  return (i18n.resolvedLanguage === 'en' ? 'en' : 'es') as Language;
}

/** Retrasa un valor: usado en los buscadores para no consultar en cada tecla. */
export function useDebounced<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);

  return debounced;
}

/** Fija el titulo del documento; util para el SEO y el historial (RNF-11). */
export function useDocumentTitle(title: string, standalone = false): void {
  useEffect(() => {
    const previous = document.title;
    // `standalone`: el titulo ya es completo (la portada) y no lleva sufijo.
    document.title = standalone ? title : title ? `${title} · CaliPeak` : 'CaliPeak';
    return () => {
      document.title = previous;
    };
  }, [title, standalone]);
}

/** true mientras el navegador esta sin conexion (RNF-10). */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  return online;
}
